import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import {
    Box,
    Chip,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    LayersOutlined as LayersOutlinedIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    WarningAmber as WarningAmberIcon,
    HourglassEmptyOutlined as HourglassEmptyOutlinedIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';

import { SCUILoadingLinear, SCUINoRecordComponent } from '../../../atomic/atoms';
import SCUIDownloadButton from '../../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIImagePreviewModal } from '../../../atomic/atoms/SCUIImagePreviewModal/SCUIImagePreviewModal';

import { MatchingSetPendingReportQuery } from '../../../services/studio/StudioQuery/MatchingSetPendingReport/MatchingSetPendingReportQuery.services';
import { downloadExcel } from '../../../utils/excelFileHelper';

const EXPORT_PAGE_SIZE = 500;

type PendingState = 'Waiting' | 'TakeAction' | 'SetCompleted' | 'PairedLate' | 'PartialShooting';

type MatchingSetPendingRow = {
    id: string;
    specialCode: string;
    colorCode: string;
    keyCode: string;
    barcode: number;
    productName: string;
    productImageUrl: string;
    addressBarcode: string;
    productAcceptanceDate: string;
    activeStatus: string;
    statusChangedAt: string;
    setOptionId: number;
    waitingSince: string;
    partnerArrivedAt: string;
    pendingState: PendingState;
    groupKey: number;
    isParent: boolean;
    arrivalOrder: number;
    hasPartnerInReport: boolean;
    isPartialShooting: boolean;
};

type MatchingSetGroup = {
    groupKey: number;
    parent: MatchingSetPendingRow;
    children: MatchingSetPendingRow[];
    isPartialShooting: boolean;
};

const pick = (item: Record<string, unknown>, camel: string, pascal: string) =>
    (item[camel] ?? item[pascal] ?? '') as string | number | boolean | null;

const formatDisplayDate = (value: unknown): string => {
    if (value == null || value === '') {
        return '';
    }

    const raw = typeof value === 'string' || typeof value === 'number' || value instanceof Date ? value : '';
    if (raw === '') {
        return '';
    }

    const date = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const mapRow = (item: Record<string, unknown>, index: number): MatchingSetPendingRow => {
    const barcode = Number(pick(item, 'barcode', 'Barcode') || 0);
    const specialCode = String(pick(item, 'specialCode', 'SpecialCode') || '');
    const colorCode = String(pick(item, 'colorCode', 'ColorCode') || '');
    const groupKey = Number(pick(item, 'groupKey', 'GroupKey') || 0);

    return {
        id: `${groupKey}-${barcode}-${specialCode}-${colorCode}-${index}`,
        specialCode,
        colorCode,
        keyCode: String(pick(item, 'keyCode', 'KeyCode') || ''),
        barcode,
        productName: String(pick(item, 'productName', 'ProductName') || ''),
        productImageUrl: String(pick(item, 'productImageUrl', 'ProductImageUrl') || ''),
        addressBarcode: String(pick(item, 'addressBarcode', 'AddressBarcode') || ''),
        productAcceptanceDate: formatDisplayDate(pick(item, 'productAcceptanceDate', 'ProductAcceptanceDate')),
        activeStatus: String(pick(item, 'activeStatusName', 'ActiveStatusName') || '').trim(),
        statusChangedAt: formatDisplayDate(pick(item, 'statusChangedAt', 'StatusChangedAt')),
        setOptionId: Number(pick(item, 'setOptionId', 'SetOptionId') || 0),
        waitingSince: formatDisplayDate(pick(item, 'waitingSince', 'WaitingSince')),
        partnerArrivedAt: formatDisplayDate(pick(item, 'partnerArrivedAt', 'PartnerArrivedAt')),
        pendingState: String(pick(item, 'pendingState', 'PendingState') || 'Waiting') as PendingState,
        groupKey,
        isParent: Boolean(pick(item, 'isParent', 'IsParent')),
        arrivalOrder: Number(pick(item, 'arrivalOrder', 'ArrivalOrder') || 0),
        hasPartnerInReport: Boolean(pick(item, 'hasPartnerInReport', 'HasPartnerInReport')),
        isPartialShooting: Boolean(pick(item, 'isPartialShooting', 'IsPartialShooting')),
    };
};

const buildGroups = (rows: MatchingSetPendingRow[]): MatchingSetGroup[] => {
    const byGroup = new Map<number, MatchingSetPendingRow[]>();

    rows.forEach((row) => {
        const list = byGroup.get(row.groupKey) ?? [];
        list.push(row);
        byGroup.set(row.groupKey, list);
    });

    const groups = Array.from(byGroup.entries()).map(([groupKey, items]) => {
        const parents = [...items.filter((x) => x.isParent)].sort((a, b) => b.arrivalOrder - a.arrivalOrder);
        const children = items.filter((x) => !x.isParent);
        const fallbackParent = [...items].sort((a, b) => (a.waitingSince < b.waitingSince ? 1 : -1))[0];
        const parent = parents[0] ?? fallbackParent;
        const isPartialShooting = items.some((x) => x.isPartialShooting);

        return {
            groupKey,
            parent,
            children: children.filter((c) => c.id !== parent.id),
            isPartialShooting,
        };
    });

    return [...groups].sort((a, b) => (a.parent.waitingSince < b.parent.waitingSince ? 1 : -1));
};

const StatusChip = ({ state, t }: { state: PendingState; t: (key: string) => string }) => {
    if (state === 'TakeAction') {
        return (
            <Chip
                size="small"
                color="warning"
                icon={<WarningAmberIcon />}
                label={t('studio.matchingSetPendingReport.statusTakeAction')}
            />
        );
    }

    if (state === 'SetCompleted' || state === 'PairedLate') {
        return (
            <Chip
                size="small"
                color="success"
                icon={<CheckCircleOutlineIcon />}
                label={t('studio.matchingSetPendingReport.statusSetCompleted')}
            />
        );
    }

    if (state === 'PartialShooting') {
        return (
            <Chip
                size="small"
                color="error"
                icon={<ErrorOutlineIcon />}
                label={t('studio.matchingSetPendingReport.statusPartialShooting')}
            />
        );
    }

    return (
        <Chip
            size="small"
            color="default"
            icon={<HourglassEmptyOutlinedIcon />}
            label={t('studio.matchingSetPendingReport.statusWaiting')}
        />
    );
};

const ProductCells = ({
    row,
    onImageClick,
    enlargeHint,
}: {
    row: MatchingSetPendingRow;
    onImageClick: (imageUrl: string) => void;
    enlargeHint: string;
}) => (
    <>
        <TableCell sx={{ width: 60, py: 1 }}>
            {row.productImageUrl ? (
                <Tooltip title={enlargeHint} arrow placement="top">
                    <Box
                        component="img"
                        src={row.productImageUrl}
                        alt={row.specialCode}
                        onClick={() => onImageClick(row.productImageUrl)}
                        sx={{
                            width: 44,
                            height: 44,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid #E5E7EB',
                            display: 'block',
                            cursor: 'zoom-in',
                            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': {
                                transform: 'scale(1.08)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            },
                        }}
                    />
                </Tooltip>
            ) : (
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1,
                        border: '1px solid #E5E7EB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#F3F4F6',
                        color: '#9CA3AF',
                        fontSize: '0.75rem',
                    }}
                >
                    -
                </Box>
            )}
        </TableCell>
        <TableCell>{row.specialCode}</TableCell>
        <TableCell>{row.colorCode}</TableCell>
        <TableCell>{row.keyCode}</TableCell>
        <TableCell>{row.barcode || ''}</TableCell>
        <TableCell>{row.productName}</TableCell>
        <TableCell>{row.productAcceptanceDate}</TableCell>
        <TableCell>{row.activeStatus}</TableCell>
        <TableCell>{row.addressBarcode || '-'}</TableCell>
        <TableCell>{row.statusChangedAt}</TableCell>
    </>
);

const getStatusLabel = (state: PendingState, t: (key: string) => string): string => {
    switch (state) {
        case 'TakeAction':
            return t('studio.matchingSetPendingReport.statusTakeAction');
        case 'SetCompleted':
        case 'PairedLate':
            return t('studio.matchingSetPendingReport.statusSetCompleted');
        case 'PartialShooting':
            return t('studio.matchingSetPendingReport.statusPartialShooting');
        default:
            return t('studio.matchingSetPendingReport.statusWaiting');
    }
};

const toExcelRow = (row: MatchingSetPendingRow, t: (key: string) => string) => ({
    specialCode: row.specialCode,
    colorCode: row.colorCode,
    keyCode: row.keyCode,
    barcode: row.barcode,
    productName: row.productName,
    productAcceptanceDate: row.productAcceptanceDate,
    activeStatus: row.activeStatus,
    addressBarcode: row.addressBarcode || '-',
    statusChangedAt: row.statusChangedAt,
    status: getStatusLabel(row.pendingState, t),
});

export const MatchingSetPendingReport = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<MatchingSetPendingRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>({});
    const [lastRefreshAt, setLastRefreshAt] = useState<string>('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const groups = useMemo(() => buildGroups(rows), [rows]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await MatchingSetPendingReportQuery.GetMatchingSetPendingProducts({
                Page: page + 1,
                PageSize: pageSize,
            });

            if (response?.errors) {
                const errorMessages = Object.values(response.errors).flat().map(String).join('\n');
                void Swal.fire(errorMessages, '', 'error');
                setRows([]);
                setTotalCount(0);
                return;
            }

            const payload = response?.data ?? response?.Data ?? response;
            const rawItems = payload?.items || payload?.Items || response?.items || response?.Items || [];

            const total = Number(
                payload?.totalCount ??
                    payload?.TotalCount ??
                    response?.totalCount ??
                    response?.TotalCount ??
                    rawItems.length
            );

            setRows(rawItems.map((item: Record<string, unknown>, index: number) => mapRow(item, index)));
            setTotalCount(Number.isNaN(total) ? rawItems.length : total);
            setLastRefreshAt(formatDisplayDate(new Date()));
        } catch {
            void Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studio.matchingSetPendingReport.fetchError'),
            });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, t]);

    useEffect(() => {
        void fetchData();
    }, [fetchData]);

    const toggleGroup = (groupKey: number) => {
        setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    const handleExcelExport = async () => {
        if (totalCount <= 0) {
            void Swal.fire({
                icon: 'info',
                title: t('general.noData'),
                showConfirmButton: false,
                timer: 1500,
            });
            return [];
        }

        const totalPages = Math.max(1, Math.ceil(totalCount / EXPORT_PAGE_SIZE));
        const aggregated: Record<string, unknown>[] = [];

        void Swal.fire({
            title: t('studio.matchingSetPendingReport.exportLoading'),
            html: t('studio.matchingSetPendingReport.exportProgress', { current: 1, total: totalPages }),
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        try {
            for (let currentPage = 1; currentPage <= totalPages; currentPage += 1) {
                const progressHtml = t('studio.matchingSetPendingReport.exportProgress', {
                    current: currentPage,
                    total: totalPages,
                });
                const content = Swal.getHtmlContainer();
                if (content) {
                    content.innerHTML = progressHtml;
                }

                const response = await MatchingSetPendingReportQuery.GetMatchingSetPendingProducts({
                    Page: currentPage,
                    PageSize: EXPORT_PAGE_SIZE,
                    SkipTotalCount: currentPage > 1,
                });

                if (response?.errors) {
                    const errorMessages = Object.values(response.errors).flat().map(String).join('\n');
                    throw new Error(errorMessages);
                }

                const rawItems = response?.items || response?.Items || [];
                aggregated.push(...rawItems);

                if (rawItems.length < EXPORT_PAGE_SIZE) {
                    break;
                }
            }

            Swal.close();

            const excelRows = aggregated
                .map((item: Record<string, unknown>, index: number) => mapRow(item, index))
                .map((row) => toExcelRow(row, t));

            if (!excelRows.length) {
                void Swal.fire({
                    icon: 'info',
                    title: t('general.noData'),
                    showConfirmButton: false,
                    timer: 1500,
                });
                return [];
            }

            downloadExcel({
                excelDownloadData: excelRows,
                excelDownloadFolderName: 'MatchingSetPendingReport',
                fileNameOverride: 'matching-set-pending-report',
            });
        } catch {
            Swal.close();
            void Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studio.matchingSetPendingReport.fetchError'),
            });
        }

        return [];
    };

    return (
        <SCUIContent>
            <SCUIBreadCrumb />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <LayersOutlinedIcon color="primary" fontSize="large" />
                <Typography variant="h5">{t('studio.matchingSetPendingReport.title')}</Typography>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {t('studio.matchingSetPendingReport.autoRefreshHint')}
                    {lastRefreshAt
                        ? ` ${t('studio.matchingSetPendingReport.lastRefresh', { time: lastRefreshAt })}`
                        : ''}
                </Typography>
            </Paper>

            {loading && <SCUILoadingLinear />}

            {!loading && groups.length === 0 && <SCUINoRecordComponent />}

            {!loading && groups.length > 0 && (
                <Paper>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LayersOutlinedIcon color="primary" />
                            <Typography variant="h6">{t('studio.matchingSetPendingReport.gridTitle')}</Typography>
                            <Chip size="small" label={totalCount} sx={{ ml: 1 }} />
                        </Box>
                        <SCUIDownloadButton
                            position="right"
                            onclick={handleExcelExport}
                            excelData={{
                                excelDownloadData: [],
                                excelDownloadFolderName: 'MatchingSetPendingReport',
                                excelDownloadButtonText: t('general.downloadExcel'),
                            }}
                        />
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={48} />
                                    <TableCell>{t('studio.matchingSetPendingReport.productImage')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.specialCode')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.colorCode')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.keyCode')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.barcode')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.productName')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.productAcceptanceDate')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.activeStatus')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.addressBarcode')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.statusChangedAt')}</TableCell>
                                    <TableCell>{t('studio.matchingSetPendingReport.status')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groups.map((group) => {
                                    const hasChildren = group.children.length > 0;
                                    const expanded = Boolean(expandedGroups[group.groupKey]);
                                    const isPartial = group.isPartialShooting;

                                    return (
                                        <Fragment key={group.groupKey}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    backgroundColor: isPartial ? '#fef2f2' : undefined,
                                                    '&:hover': {
                                                        backgroundColor: isPartial ? '#fee2e2 !important' : undefined,
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    {hasChildren ? (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleGroup(group.groupKey)}
                                                            title={t('studio.matchingSetPendingReport.togglePartner')}
                                                        >
                                                            {expanded ? (
                                                                <KeyboardArrowUpIcon />
                                                            ) : (
                                                                <KeyboardArrowDownIcon />
                                                            )}
                                                        </IconButton>
                                                    ) : null}
                                                </TableCell>
                                                <ProductCells
                                                    row={group.parent}
                                                    onImageClick={setPreviewImage}
                                                    enlargeHint={t('studio.clickToEnlarge')}
                                                />
                                                <TableCell>
                                                    <StatusChip state={group.parent.pendingState} t={t} />
                                                </TableCell>
                                            </TableRow>
                                            {hasChildren && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={12}
                                                        sx={{
                                                            py: 0,
                                                            borderBottom: expanded ? undefined : 0,
                                                            backgroundColor: isPartial ? '#fef2f2' : undefined,
                                                        }}
                                                    >
                                                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                            <Box
                                                                sx={{
                                                                    m: 1,
                                                                    ml: 6,
                                                                    p: 1.5,
                                                                    borderRadius: 1,
                                                                    backgroundColor: isPartial ? '#fff5f5' : undefined,
                                                                    border: isPartial ? '1px solid #fca5a5' : undefined,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    color={isPartial ? 'error' : 'text.secondary'}
                                                                    sx={{
                                                                        mb: 1,
                                                                        fontWeight: isPartial ? 600 : 400,
                                                                    }}
                                                                >
                                                                    {t(
                                                                        'studio.matchingSetPendingReport.partnerProducts'
                                                                    )}
                                                                </Typography>
                                                                <Table size="small">
                                                                    <TableBody>
                                                                        {group.children.map((child) => (
                                                                            <TableRow
                                                                                key={child.id}
                                                                                sx={{
                                                                                    backgroundColor: isPartial
                                                                                        ? '#fee2e2'
                                                                                        : 'action.hover',
                                                                                }}
                                                                            >
                                                                                <ProductCells
                                                                                    row={child}
                                                                                    onImageClick={setPreviewImage}
                                                                                    enlargeHint={t(
                                                                                        'studio.clickToEnlarge'
                                                                                    )}
                                                                                />
                                                                                <TableCell>
                                                                                    <StatusChip
                                                                                        state={child.pendingState}
                                                                                        t={t}
                                                                                    />
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(e) => {
                            setPageSize(Number.parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}

            <SCUIImagePreviewModal imageUrl={previewImage} onClose={() => setPreviewImage(null)} />
        </SCUIContent>
    );
};
